/**
 * Auto-rig skeleton fitters (humanoid + quadruped) for arbitrary triangle
 * meshes, ported from the cross-section fitter used to rig the library human
 * (`fit_skeleton.cjs`). Everything works from true cross-sections of the mesh
 * (plane / triangle intersection loops), so mesh density does not matter:
 *  - spine / neck / head: horizontal section loops around the midline
 *  - legs: horizontal leg loops; feet from the sole vertices
 *  - arms: section loops perpendicular to the shoulder->hand axis, tracked
 *    from the shoulder to the fingertips; wrist = thinnest forearm loop
 *  - fingers / thumb: separate mesh components past the knuckles / thumb web
 *
 * The mesh is first welded (UV / normal seams split vertices in real assets),
 * then moved into a canonical frame (feet on y = 0, midline on x = 0, facing
 * -Z, character left on +X; the engine's convention) where the original
 * fitter runs; results are transformed back to model space. Pure functions:
 * no DOM, never throws (falls back to template proportions plus warnings).
 */
import { decompose, type RigJoint, type Vec3 } from '../rig-doc.js';

// ---------------------------------------------------------------------------
// Public types

export interface AutoRigMesh {
  /** Model-space vertex positions (+Y up). */
  positions: Vec3[];
  /** Triangle list (3 indices per triangle). */
  indices: number[];
}
export type Facing = 'auto' | '+z' | '-z';
export interface FitHumanOptions {
  /** Finger + thumb chains (default true). */
  fingers?: boolean;
  /** Mid-bone twist joints on upper arm, lower arm, upper leg, lower leg (default true). */
  twistBones?: boolean;
  /** breast / breastTip joints per side (default false). */
  breasts?: boolean;
  /** toes / toeTip joints per foot (default true). */
  toes?: boolean;
  /** Joints between hips and neck (default 3: spine, chest, upperChest). */
  spineCount?: number;
  /** Which way the character faces; 'auto' detects it from the feet (default). */
  facing?: Facing;
}
export interface FitQuadrupedOptions {
  /** Joints between hips and chest (default 2: spine, spine.001). */
  spineCount?: number;
  /** Neck joints between chest and head (default 2). */
  neckCount?: number;
  /** Tail joints (default 4; 0 disables the tail). */
  tailCount?: number;
  /** Toe joint at the front of each foot (default true). */
  toes?: boolean;
  /** Jaw joint under the head (default false). */
  jaw?: boolean;
}
/** Intermediate fitted joint (model space), as produced by the source fitter. */
export interface FittedJoint {
  name: string;
  parent: string | null;
  position: Vec3;
  radius: number;
}
export interface AutoRigResult {
  /** Rig joints with global bind transforms (rotation: Euler XYZ degrees, see rig-doc). */
  joints: RigJoint[];
  fitted: FittedJoint[];
  warnings: string[];
  /** Detected forward direction of the character (model space, unit). */
  forward: Vec3;
  /** Detected height (model units). */
  height: number;
  /** Ground height (lowest vertex y). */
  groundY: number;
}

// ---------------------------------------------------------------------------
// Vector helpers

const add = (a: Vec3, b: Vec3): Vec3 => [a[0] + b[0], a[1] + b[1], a[2] + b[2]];
const sub = (a: Vec3, b: Vec3): Vec3 => [a[0] - b[0], a[1] - b[1], a[2] - b[2]];
const mul = (a: Vec3, k: number): Vec3 => [a[0] * k, a[1] * k, a[2] * k];
const dot = (a: Vec3, b: Vec3): number => a[0] * b[0] + a[1] * b[1] + a[2] * b[2];
const len = (a: Vec3): number => Math.hypot(a[0], a[1], a[2]);
const dist = (a: Vec3, b: Vec3): number => Math.hypot(a[0] - b[0], a[1] - b[1], a[2] - b[2]);
const lerp = (a: Vec3, b: Vec3, t: number): Vec3 => [
  a[0] + (b[0] - a[0]) * t,
  a[1] + (b[1] - a[1]) * t,
  a[2] + (b[2] - a[2]) * t,
];
const cross = (a: Vec3, b: Vec3): Vec3 => [
  a[1] * b[2] - a[2] * b[1],
  a[2] * b[0] - a[0] * b[2],
  a[0] * b[1] - a[1] * b[0],
];
const norm = (a: Vec3): Vec3 => {
  const l = len(a);
  return l > 1e-12 ? mul(a, 1 / l) : [0, 0, 0];
};
const finite = (v: Vec3): boolean => v.every((x) => Number.isFinite(x));
const clamp = (x: number, lo: number, hi: number): number => Math.max(lo, Math.min(hi, x));
const mean = (pts: Vec3[], fallback: Vec3): Vec3 =>
  pts.length
    ? mul(
        pts.reduce<Vec3>((a, p) => add(a, p), [0, 0, 0]),
        1 / pts.length,
      )
    : fallback;

// ---------------------------------------------------------------------------
// Mesh preparation + cross sections

interface Mesh {
  P: Vec3[];
  /** Flat triangle indices. */
  tris: number[];
  adj?: number[][];
}
interface Loop {
  pts: Vec3[];
  /** Mesh vertices of the crossed edges (both endpoints). */
  verts: number[];
  c: Vec3;
  r: number;
  x0: number;
  x1: number;
  y0: number;
  y1: number;
  z0: number;
  z1: number;
  w: number;
  dz: number;
}

/** Welds coincident vertices (seams) and drops invalid / degenerate triangles. */
function weld(mesh: AutoRigMesh): Mesh {
  const src = Array.isArray(mesh?.positions) ? mesh.positions : [];
  const idx = Array.isArray(mesh?.indices) ? mesh.indices : [];
  let lo = Infinity,
    hi = -Infinity;
  for (const p of src)
    if (p && finite(p))
      for (const v of p) {
        lo = Math.min(lo, v);
        hi = Math.max(hi, v);
      }
  const eps = Number.isFinite(hi - lo) && hi > lo ? (hi - lo) * 1e-6 : 1e-6;
  const remap = new Int32Array(src.length).fill(-1);
  const keys = new Map<string, number>();
  const P: Vec3[] = [];
  src.forEach((p, i) => {
    if (!p || !finite(p)) return;
    const key = `${Math.round(p[0] / eps)},${Math.round(p[1] / eps)},${Math.round(p[2] / eps)}`;
    let id = keys.get(key);
    if (id === undefined) {
      id = P.length;
      P.push([p[0], p[1], p[2]]);
      keys.set(key, id);
    }
    remap[i] = id;
  });
  const tris: number[] = [];
  for (let t = 0; t + 2 < idx.length; t += 3) {
    const a = remap[idx[t]!] ?? -1,
      b = remap[idx[t + 1]!] ?? -1,
      c = remap[idx[t + 2]!] ?? -1;
    if (a < 0 || b < 0 || c < 0 || a === b || b === c || a === c) continue;
    tris.push(a, b, c);
  }
  return { P, tris };
}

/** Cross-section loops of the plane n.p = d (optionally over a triangle subset). */
function section(m: Mesh, n: Vec3, d: number, subset?: number[]): Loop[] {
  const P = m.P,
    N = P.length,
    T = subset ?? m.tris;
  const sd = new Float64Array(N).fill(NaN);
  const s = (i: number): number => {
    let v = sd[i]!;
    if (Number.isNaN(v)) {
      v = dot(P[i]!, n) - d;
      sd[i] = v;
    }
    return v;
  };
  const edgePt = new Map<number, number>();
  const pts: Vec3[] = [];
  const verts: number[] = [];
  const parent: number[] = [];
  const find = (x: number): number => {
    while (parent[x] !== x) {
      parent[x] = parent[parent[x]!]!;
      x = parent[x]!;
    }
    return x;
  };
  const hit: number[] = [];
  for (let t = 0; t + 2 < T.length; t += 3) {
    const tri = [T[t]!, T[t + 1]!, T[t + 2]!] as const;
    const s0 = s(tri[0]),
      s1 = s(tri[1]),
      s2 = s(tri[2]);
    if ((s0 > 0 && s1 > 0 && s2 > 0) || (s0 <= 0 && s1 <= 0 && s2 <= 0)) continue;
    hit.length = 0;
    for (let k = 0; k < 3; k++) {
      const a = tri[k]!,
        b = tri[(k + 1) % 3]!;
      const sa = s(a),
        sb = s(b);
      if (sa > 0 === sb > 0) continue;
      const key = a < b ? a * N + b : b * N + a;
      let id = edgePt.get(key);
      if (id === undefined) {
        const u = sa / (sa - sb);
        id = pts.length;
        pts.push(lerp(P[a]!, P[b]!, u));
        verts.push(a, b);
        parent.push(id);
        edgePt.set(key, id);
      }
      hit.push(id);
    }
    if (hit.length === 2) {
      const ra = find(hit[0]!),
        rb = find(hit[1]!);
      if (ra !== rb) parent[ra] = rb;
    }
  }
  const groups = new Map<number, number[]>();
  for (let i = 0; i < pts.length; i++) {
    const r = find(i);
    const g = groups.get(r);
    if (g) g.push(i);
    else groups.set(r, [i]);
  }
  const loops: Loop[] = [];
  for (const g of groups.values()) {
    if (g.length < 3) continue;
    const gp = g.map((i) => pts[i]!);
    const c = mean(gp, [0, 0, 0]);
    const r = gp.reduce((a, p) => a + dist(p, c), 0) / gp.length;
    let x0 = Infinity,
      x1 = -Infinity,
      y0 = Infinity,
      y1 = -Infinity,
      z0 = Infinity,
      z1 = -Infinity;
    for (const p of gp) {
      x0 = Math.min(x0, p[0]);
      x1 = Math.max(x1, p[0]);
      y0 = Math.min(y0, p[1]);
      y1 = Math.max(y1, p[1]);
      z0 = Math.min(z0, p[2]);
      z1 = Math.max(z1, p[2]);
    }
    const lv: number[] = [];
    for (const i of g) lv.push(verts[2 * i]!, verts[2 * i + 1]!);
    loops.push({ pts: gp, verts: lv, c, r, x0, x1, y0, y1, z0, z1, w: x1 - x0, dz: z1 - z0 });
  }
  return loops;
}

function adjacency(m: Mesh): number[][] {
  if (m.adj) return m.adj;
  const adj: number[][] = Array.from({ length: m.P.length }, () => []);
  for (let t = 0; t + 2 < m.tris.length; t += 3)
    for (let k = 0; k < 3; k++) {
      const a = m.tris[t + k]!,
        b = m.tris[t + ((k + 1) % 3)]!;
      adj[a]!.push(b);
      adj[b]!.push(a);
    }
  m.adj = adj;
  return adj;
}

/** Connected components of a vertex subset, largest first. */
function components(m: Mesh, ids: number[]): number[][] {
  const adj = adjacency(m);
  const set = new Set(ids),
    seen = new Set<number>(),
    out: number[][] = [];
  for (const st of ids) {
    if (seen.has(st)) continue;
    const c = [st];
    seen.add(st);
    for (let k = 0; k < c.length; k++)
      for (const nb of adj[c[k]!]!)
        if (set.has(nb) && !seen.has(nb)) {
          seen.add(nb);
          c.push(nb);
        }
    out.push(c);
  }
  return out.sort((x, y) => y.length - x.length);
}

interface Bounds {
  min: Vec3;
  max: Vec3;
}
function bounds(P: Vec3[]): Bounds {
  const min: Vec3 = [Infinity, Infinity, Infinity],
    max: Vec3 = [-Infinity, -Infinity, -Infinity];
  for (const p of P)
    for (let k = 0; k < 3; k++) {
      min[k] = Math.min(min[k]!, p[k]!);
      max[k] = Math.max(max[k]!, p[k]!);
    }
  return { min, max };
}

/** Rigid frame: canonical = Ry(angle) * (p - origin); angle in quarter turns. */
interface Frame {
  origin: Vec3;
  cos: number;
  sin: number;
}
const frame = (origin: Vec3, quarterTurns: number): Frame => {
  const q = ((quarterTurns % 4) + 4) % 4;
  return { origin, cos: [1, 0, -1, 0][q]!, sin: [0, 1, 0, -1][q]! };
};
const toCanon = (f: Frame, p: Vec3): Vec3 => {
  const x = p[0] - f.origin[0],
    y = p[1] - f.origin[1],
    z = p[2] - f.origin[2];
  return [f.cos * x + f.sin * z, y, -f.sin * x + f.cos * z];
};
const toModel = (f: Frame, p: Vec3): Vec3 => [
  f.cos * p[0] - f.sin * p[2] + f.origin[0],
  p[1] + f.origin[1],
  f.sin * p[0] + f.cos * p[2] + f.origin[2],
];
const canonMesh = (m: Mesh, f: Frame): Mesh => ({ P: m.P.map((p) => toCanon(f, p)), tris: m.tris });

/** Largest-area loop of `loops` that straddles x = 0 (within tol). */
const straddling = (loops: Loop[], tol: number, area: (l: Loop) => number): Loop | null => {
  let best: Loop | null = null;
  for (const l of loops) if (l.x0 < tol && l.x1 > -tol && (!best || area(l) > area(best))) best = l;
  return best;
};

// ---------------------------------------------------------------------------
// Naming

const SIDES: [1 | -1, '.L' | '.R'][] = [
  [1, '.L'],
  [-1, '.R'],
];
const FINGERS = ['index', 'middle', 'ring', 'pinky'] as const;

/** base, base.001, base.002, … (Blender numbering). */
const chain = (base: string, count: number): string[] =>
  Array.from({ length: Math.max(0, Math.round(count)) }, (_, k) =>
    k ? `${base}.${String(k).padStart(3, '0')}` : base,
  );
/** Spine joints above the hips: chest / spine+chest / spine…+chest+upperChest. */
function spineNames(count: number): string[] {
  const n = clamp(Math.round(count), 1, 12);
  if (n === 1) return ['chest'];
  if (n === 2) return ['spine', 'chest'];
  return [...chain('spine', n - 2), 'chest', 'upperChest'];
}

const humanDefaults = (o: FitHumanOptions = {}) => ({
  fingers: o.fingers ?? true,
  twistBones: o.twistBones ?? true,
  breasts: o.breasts ?? false,
  toes: o.toes ?? true,
  spineCount: clamp(Math.round(Number.isFinite(o.spineCount) ? o.spineCount! : 3), 1, 12),
  facing: o.facing ?? 'auto',
});
const quadDefaults = (o: FitQuadrupedOptions = {}) => ({
  spineCount: clamp(Math.round(Number.isFinite(o.spineCount) ? o.spineCount! : 2), 0, 12),
  neckCount: clamp(Math.round(Number.isFinite(o.neckCount) ? o.neckCount! : 2), 1, 12),
  tailCount: clamp(Math.round(Number.isFinite(o.tailCount) ? o.tailCount! : 4), 0, 32),
  toes: o.toes ?? true,
  jaw: o.jaw ?? false,
});

/** Swaps a `.L` / `.R` suffix (null when the name has none). */
export function mirrorName(name: string): string | null {
  if (name.endsWith('.L')) return name.slice(0, -2) + '.R';
  if (name.endsWith('.R')) return name.slice(0, -2) + '.L';
  return null;
}

// ---------------------------------------------------------------------------
// Humanoid

/**
 * Fits a humanoid skeleton. Joint names follow the library human template
 * (hips, spine, chest, upperChest, neck, head, headTop, clavicle.L, upperArm.L,
 * upperArm_twist.L, lowerArm.L, lowerArm_twist.L, hand.L, thumb1-3/Tip.L,
 * index1-3/Tip.L ..., upperLeg.L, upperLeg_twist.L, lowerLeg.L,
 * lowerLeg_twist.L, foot.L, toes.L, toeTip.L, breast.L, breastTip.L). `.L` is
 * the character's left (+X when facing -Z, the engine convention).
 */
export function fitHuman(mesh: AutoRigMesh, options: FitHumanOptions = {}): AutoRigResult {
  const o = humanDefaults(options);
  const warnings: string[] = [];
  const warn = (s: string) => {
    if (!warnings.includes(s)) warnings.push(s);
  };
  let m: Mesh = { P: [], tris: [] };
  try {
    m = weld(mesh);
  } catch {
    warn('Mesh could not be read; using template proportions.');
  }
  if (m.P.length < 4 || m.tris.length < 3) warn('Mesh is empty; using template proportions.');
  const b = bounds(m.P);
  let H = b.max[1] - b.min[1];
  const groundY = Number.isFinite(b.min[1]) ? b.min[1] : 0;
  if (!(H > 1e-9) || !Number.isFinite(H)) H = 1.75;
  const midX0 = Number.isFinite(b.min[0]) ? (b.min[0] + b.max[0]) / 2 : 0;
  const midZ0 = Number.isFinite(b.min[2]) ? (b.min[2] + b.max[2]) / 2 : 0;

  try {
    // Midline: the torso's centre (arms / props can unbalance the bounding box).
    let f = frame([midX0, groundY, midZ0], 0);
    let cm = canonMesh(m, f);
    const sc = H / 1.75;
    const centres: number[] = [];
    for (const y of [0.55, 0.62, 0.7]) {
      const t = straddling(section(cm, [0, 1, 0], y * H), 0.01 * sc, (l) => l.w * l.dz);
      if (t && t.w < 0.5 * H) centres.push((t.x0 + t.x1) / 2);
    }
    if (centres.length) {
      const dx = centres.reduce((a, x) => a + x, 0) / centres.length;
      f = frame([midX0 + dx, groundY, midZ0], 0);
      cm = canonMesh(m, f);
    }
    // Facing: the feet extend further towards the toes than the heels.
    let facing = o.facing;
    if (facing === 'auto') {
      const score = facingFromFeet(cm, H);
      if (score > 0.015 * sc) facing = '+z';
      else if (score < -0.015 * sc) facing = '-z';
      else {
        facing = '-z';
        warn('Could not detect facing from the feet; assuming the character faces -Z.');
      }
    }
    if (facing === '+z') {
      f = frame(f.origin, 2);
      cm = canonMesh(m, f);
    }
    const fitted = fitHumanCanonical(cm, H, o, warn).map((j) => ({
      ...j,
      position: toModel(f, j.position),
    }));
    const forward = norm(sub(toModel(f, [0, 0, -1]), toModel(f, [0, 0, 0])));
    return finish(fitted, forward, H, groundY, warnings);
  } catch (e) {
    warn(
      `Fitting failed (${e instanceof Error ? e.message : String(e)}); using template proportions.`,
    );
    const f = frame([midX0, groundY, midZ0], 0);
    const fitted = fitHumanCanonical({ P: [], tris: [] }, H, o, () => {}).map((j) => ({
      ...j,
      position: toModel(f, j.position),
    }));
    return finish(fitted, [0, 0, -1], H, groundY, warnings);
  }
}

/** Positive when the toes point to +Z (canonical frame before any flip). */
function facingFromFeet(m: Mesh, H: number): number {
  const sc = H / 1.75;
  let score = 0;
  for (const [s] of SIDES) {
    const kneeY = 0.285 * H;
    const legs = section(m, [0, 1, 0], kneeY).filter(
      (l) => l.c[0] * s > 0.02 * sc && l.x0 * l.x1 > 0,
    );
    const knee = legs.sort((a, b) => Math.abs(a.c[0]) - Math.abs(b.c[0]))[0];
    if (!knee) continue;
    let z0 = Infinity,
      z1 = -Infinity;
    for (const p of m.P)
      if (p[1] < 0.03 * sc && p[0] * s > 0.02 * sc && Math.abs(p[0] - knee.c[0]) < 0.15 * sc) {
        z0 = Math.min(z0, p[2]);
        z1 = Math.max(z1, p[2]);
      }
    if (!Number.isFinite(z0)) continue;
    score += z1 - knee.c[2] - (knee.c[2] - z0);
  }
  return score;
}

type HumanOpts = ReturnType<typeof humanDefaults>;

/** The source fitter, in the canonical frame (+Y up, -Z front, x = 0 midline, feet on y = 0). */
function fitHumanCanonical(
  m: Mesh,
  H: number,
  o: HumanOpts,
  warn: (s: string) => void,
): FittedJoint[] {
  const P = m.P,
    N = P.length,
    sc = H / 1.75;
  const UP: Vec3 = [0, 1, 0];
  const J: FittedJoint[] = [];
  const addJ = (name: string, parent: string | null, pos: Vec3, radius: number): Vec3 => {
    J.push({
      name,
      parent,
      position: [pos[0], pos[1], pos[2]],
      radius: Math.max(radius, 0.003 * sc),
    });
    return pos;
  };
  const cache = new Map<number, Loop[]>();
  const hsec = (y: number): Loop[] => {
    let l = cache.get(y);
    if (!l) {
      l = section(m, UP, y);
      cache.set(y, l);
    }
    return l;
  };
  const torso = (y: number) => straddling(hsec(y), 0.01 * sc, (l) => l.w * l.dz);
  const sideLoops = (y: number, s: number) =>
    hsec(y).filter((l) => l.c[0] * s > 0.02 * sc && l.x0 * l.x1 > 0);

  // ---- crotch: highest height where the body splits into two leg loops
  let crotchY = 0.47 * H,
    foundCrotch = false;
  for (let y = 0.58 * H; y > 0.35 * H; y -= 0.003 * sc)
    if (!torso(y) && sideLoops(y, 1).length && sideLoops(y, -1).length) {
      crotchY = y;
      foundCrotch = true;
      break;
    }
  if (!foundCrotch) warn('Legs not separated (crotch not found); using template proportions.');
  // ---- neck: thinnest torso loop between the chest and the head
  let neckY = 0.86 * H,
    neckW = Infinity;
  for (let y = 0.65 * H; y < 0.92 * H; y += 0.003 * sc) {
    const t = torso(y);
    if (t && t.w < neckW && t.w > 0.06 * sc) {
      neckW = t.w;
      neckY = y;
    }
  }
  if (!Number.isFinite(neckW)) {
    warn('Neck not found; using template proportions.');
    neckW = 0.11 * sc;
  }
  const tz = (y: number, f: number) => {
    const t = torso(y);
    return t ? t.z0 + t.dz * f : 0;
  };
  const tr = (y: number) => {
    const t = torso(y);
    return t ? Math.min(t.w, t.dz) * 0.42 : 0.1 * sc;
  };

  // ---- spine, neck, head
  const hipJY = crotchY + 0.045 * sc;
  const pelvisY = crotchY + 0.08 * sc;
  const chestTop = neckY - 0.055 * sc;
  const spine = ['hips', ...spineNames(o.spineCount)];
  spine.forEach((n, k) => {
    const y = pelvisY + (chestTop - pelvisY) * (k / (spine.length - 1));
    addJ(n, k ? spine[k - 1]! : null, [0, y, tz(y, 0.58)], tr(y) * (k === 0 ? 1 : 0.85));
  });
  const topSpine = spine[spine.length - 1]!;
  const neckZ = tz(neckY - 0.02 * sc, 0.55);
  addJ('neck', topSpine, [0, neckY - 0.03 * sc, neckZ + 0.004 * sc], neckW * 0.42);
  // No eye meshes in a generic soup: eye height from the head span (human ~0.53).
  const headSpan = Math.max(H - neckY, 0.05 * sc);
  const eyeY = neckY + headSpan * 0.53;
  const hs = torso(eyeY + 0.01 * sc);
  const headCZ = hs ? hs.z0 + hs.dz * 0.5 : neckZ;
  addJ(
    'head',
    'neck',
    [0, Math.max(eyeY - 0.07 * sc, neckY + 0.035 * sc), neckZ - 0.004 * sc],
    0.075 * sc,
  );
  addJ('headTop', 'head', [0, H, headCZ], 0.07 * sc);

  // ---- breasts: forward-most chest point per side
  if (o.breasts) {
    const spineJ = J.filter((j) => spine.includes(j.name));
    const yLo = pelvisY + (chestTop - pelvisY) * 0.45,
      yHi = chestTop;
    for (const [s, S] of SIDES) {
      let tip: Vec3 | null = null;
      for (const p of P)
        if (
          p[1] > yLo &&
          p[1] < yHi &&
          p[0] * s > 0.035 * sc &&
          p[0] * s < 0.17 * sc &&
          (!tip || p[2] < tip[2])
        )
          tip = p;
      if (!tip) {
        warn('Breasts not found; using template positions.');
        const y = yLo + (yHi - yLo) * 0.55;
        tip = [0.09 * sc * s, y, tz(y, 0) - 0.03 * sc];
      }
      const ty = tip[1];
      const st = torso(ty);
      const ster = P.filter((p) => Math.abs(p[0]) < 0.01 * sc && Math.abs(p[1] - ty) < 0.02 * sc);
      const wallZ = ster.length
        ? Math.min(...ster.map((p) => p[2]))
        : st
          ? st.z0
          : tip[2] + 0.03 * sc;
      const depth = Math.max(wallZ - tip[2], 0.005 * sc);
      const base: Vec3 = [tip[0] * 0.92, tip[1] + 0.012 * sc, wallZ + 0.03 * sc];
      const par = spineJ.reduce((bb, j) =>
        Math.abs(j.position[1] - base[1]) < Math.abs(bb.position[1] - base[1]) ? j : bb,
      ).name;
      addJ('breast' + S, par, base, 0.045 * sc + depth * 0.5);
      addJ(
        'breastTip' + S,
        'breast' + S,
        [tip[0], tip[1], tip[2] + 0.004 * sc],
        0.02 * sc + depth * 0.4,
      );
    }
  }

  for (const [s, S] of SIDES) {
    // ---- legs
    const leg = (y: number) =>
      sideLoops(y, s).sort((a, bb) => Math.abs(a.c[0]) - Math.abs(bb.c[0]))[0] ?? null;
    const top = leg(crotchY - 0.03 * sc);
    const hip = addJ(
      'upperLeg' + S,
      'hips',
      [top ? top.c[0] * 0.9 : 0.09 * sc * s, hipJY, tz(hipJY, 0.5)],
      top ? top.r * 0.9 : 0.075 * sc,
    );
    // Knee / ankle heights relative to the crotch (source: 0.285H / 0.055H at crotch 0.47H).
    const kneeY = crotchY * 0.606,
      ankleY = crotchY * 0.117;
    const kn = leg(kneeY),
      an = leg(ankleY);
    if (!kn || !an) warn(`Leg${S} not traced; using template proportions.`);
    const knee: Vec3 = kn ? kn.c : [hip[0], kneeY, hip[2]];
    const ankle: Vec3 = an ? an.c : [knee[0], ankleY, knee[2]];
    let par = 'upperLeg' + S;
    if (o.twistBones) {
      const tm = leg((hip[1] + knee[1]) / 2);
      addJ(
        'upperLeg_twist' + S,
        par,
        tm ? tm.c : lerp(hip, knee, 0.5),
        tm ? tm.r * 0.85 : 0.07 * sc,
      );
      par = 'upperLeg_twist' + S;
    }
    addJ('lowerLeg' + S, par, knee, kn ? kn.r * 0.85 : 0.05 * sc);
    par = 'lowerLeg' + S;
    if (o.twistBones) {
      const sm = leg((knee[1] + ankle[1]) / 2);
      addJ(
        'lowerLeg_twist' + S,
        par,
        sm ? sm.c : lerp(knee, ankle, 0.5),
        sm ? sm.r * 0.85 : 0.045 * sc,
      );
      par = 'lowerLeg_twist' + S;
    }
    addJ('foot' + S, par, ankle, an ? an.r * 0.85 : 0.035 * sc);
    if (o.toes) {
      const sole: Vec3[] = [];
      for (let i = 0; i < N; i++)
        if (
          P[i]![1] < 0.03 * sc &&
          P[i]![0] * s > 0.02 * sc &&
          Math.abs(P[i]![0] - ankle[0]) < 0.15 * sc
        )
          sole.push(P[i]!);
      let ball: Vec3, tip: Vec3;
      if (sole.length) {
        let zF = Infinity,
          zB = -Infinity;
        for (const p of sole) {
          zF = Math.min(zF, p[2]);
          zB = Math.max(zB, p[2]);
        }
        const fl = zB - zF;
        const at = (z: number, w: number): Vec3 =>
          mean(
            sole.filter((p) => Math.abs(p[2] - z) < w),
            [ankle[0], 0, z],
          );
        ball = at(zF + fl * 0.27, 0.015 * sc);
        tip = at(zF + 0.01 * sc, 0.01 * sc);
      } else {
        warn(`Sole of foot${S} not found; using template toes.`);
        ball = [ankle[0], 0, ankle[2] - 0.13 * sc];
        tip = [ankle[0], 0, ankle[2] - 0.2 * sc];
      }
      addJ('toes' + S, 'foot' + S, [ball[0], 0.025 * sc, ball[2]], 0.03 * sc);
      addJ('toeTip' + S, 'toes' + S, [tip[0], 0.018 * sc, tip[2] + 0.008 * sc], 0.018 * sc);
    }

    // ---- arm: track section loops from the shoulder to the fingertips
    const sy = neckY - 0.065 * sc;
    const sl = torso(sy);
    // A connected T-pose cuts through the arms at shoulder height: cap the
    // half width with the chest (below the armpits).
    const cl = torso(crotchY + (neckY - crotchY) * 0.6);
    let halfW = sl ? (s > 0 ? sl.x1 : -sl.x0) : 0.2 * sc;
    if (cl) halfW = Math.min(halfW, (s > 0 ? cl.x1 : -cl.x0) * 1.25);
    halfW = Math.max(halfW, 0.08 * sc);
    // Shoulder (deltoid) half width: widest torso loop a little below `sy`,
    // with the same chest cap; only used to stop the upper arm at the shoulder.
    let shoulderW = halfW;
    for (let y = sy - 0.1 * sc; y <= sy; y += 0.01 * sc) {
      const t = torso(y);
      if (t) shoulderW = Math.max(shoulderW, s > 0 ? t.x1 : -t.x0);
    }
    if (cl) shoulderW = Math.min(shoulderW, (s > 0 ? cl.x1 : -cl.x0) * 1.5);
    const shoulderGuess: Vec3 = [
      s * Math.max(halfW - 0.045 * sc, 0.03 * sc),
      sy - 0.01 * sc,
      sl ? sl.z0 + sl.dz * 0.5 : cl ? cl.z0 + cl.dz * 0.5 : 0,
    ];
    // Hand tip: farthest vertex from the shoulder on this side above the crotch.
    let tipV: Vec3 | null = null,
      td = -1;
    for (const p of P) {
      if (p[0] * s <= halfW || p[1] < crotchY) continue;
      const d = dist(p, shoulderGuess);
      if (d > td) {
        td = d;
        tipV = p;
      }
    }
    if (!tipV || td < 0.2 * sc) {
      warn(`Arm${S} not found; using template proportions.`);
      td = 0.78 * sc;
      tipV = add(shoulderGuess, [s * td, 0, 0]);
    }
    const a = norm(sub(tipV, shoulderGuess));
    const armTris: number[] = [];
    for (let t = 0; t + 2 < m.tris.length; t += 3)
      if ([0, 1, 2].some((k) => P[m.tris[t + k]!]![0] * s > halfW * 0.7))
        armTris.push(m.tris[t]!, m.tris[t + 1]!, m.tris[t + 2]!);
    const step = 0.01 * sc;
    const pick = (t: number, near: Vec3, lim = 0.05 * sc): Loop | null => {
      const ctr = add(shoulderGuess, mul(a, t));
      const loops = section(m, a, dot(ctr, a), armTris).filter(
        (l) => dist(l.c, near) < lim && l.r < 0.09 * sc && l.c[0] * s > halfW * 0.8,
      );
      return loops.sort((p, q) => dist(p.c, near) - dist(q.c, near))[0] ?? null;
    };
    // March from mid-arm (clearly separate from the body) out to the fingertips
    // and in towards the shoulder until the loop merges with the torso.
    const mid = td * 0.4;
    const m0 = pick(mid, add(shoulderGuess, mul(a, mid)), 0.3 * sc);
    interface TrackE {
      t: number;
      c: Vec3;
      r: number;
    }
    const out: TrackE[] = [],
      inn: TrackE[] = [];
    if (m0) {
      let cur = m0.c;
      for (let t = mid; t < td + 0.02 * sc; t += step) {
        const l = pick(t, cur);
        if (!l) break;
        out.push({ t, c: l.c, r: l.r });
        cur = l.c;
      }
      cur = m0.c;
      let lastR = m0.r;
      for (let t = mid - step; t > -0.05 * sc; t -= step) {
        const l = pick(t, cur);
        if (!l || l.r > lastR * 1.6 || l.x0 * l.x1 < 0) break;
        inn.push({ t, c: l.c, r: l.r });
        cur = l.c;
        lastR = Math.max(lastR, l.r);
      }
    }
    let track: TrackE[] = [...inn.reverse(), ...out];
    if (track.length < 4) {
      warn(`Arm${S} not traced; using template proportions.`);
      track = [];
      for (let t = 0.05 * sc; t <= td; t += step)
        track.push({ t, c: add(shoulderGuess, mul(a, t)), r: 0.045 * sc * (1 - (0.5 * t) / td) });
      if (track.length < 2)
        track = [
          { t: 0, c: shoulderGuess, r: 0.04 * sc },
          { t: td, c: tipV, r: 0.02 * sc },
        ];
    }
    const first = track[0]!,
      last = track[track.length - 1]!;
    const t0 = first.t;
    const span = last.t - t0;
    const trAt = (t: number) =>
      track.reduce((bb, e) => (Math.abs(e.t - t) < Math.abs(bb.t - t) ? e : bb), first);
    let wr = trAt(t0 + span * 0.7);
    for (const e of track)
      if (e.t > t0 + span * 0.58 && e.t < t0 + span * 0.82 && e.r < wr.r) wr = e;
    const sh = trAt(t0 + 0.015 * sc);
    // Elbow by forearm length back from the wrist; shoulder by upper-arm
    // length back from the elbow along the upper arm's measured direction.
    const el = trAt(wr.t - Math.min(0.146 * H * 0.97, 0.6 * (wr.t - t0)));
    let upDir = norm(sub(sh.c, el.c));
    if (len(upDir) < 0.5) upDir = mul(a, -1);
    let upper = 0.186 * H;
    // Generalised: stop at the shoulder guess (torso side minus an arm radius)
    // so stocky / stylised proportions do not push the shoulder into the chest.
    if (upDir[0] * s < -1e-6)
      upper = Math.min(
        upper,
        (el.c[0] * s - Math.max(shoulderW - 0.045 * sc, 0.06 * sc)) / (-upDir[0] * s),
      );
    upper = Math.max(upper, 0.05 * sc);
    const shoulder = add(el.c, mul(upDir, upper));
    addJ(
      'clavicle' + S,
      topSpine,
      [0.025 * sc * s, Math.min(shoulder[1] + 0.02 * sc, neckY - 0.03 * sc), tz(shoulder[1], 0.35)],
      0.04 * sc,
    );
    addJ('upperArm' + S, 'clavicle' + S, shoulder, Math.min(sh.r, 0.06 * sc) * 0.9);
    par = 'upperArm' + S;
    if (o.twistBones) {
      addJ('upperArm_twist' + S, par, lerp(shoulder, el.c, 0.5), trAt((sh.t + el.t) / 2).r * 0.9);
      par = 'upperArm_twist' + S;
    }
    addJ('lowerArm' + S, par, el.c, el.r * 0.9);
    par = 'lowerArm' + S;
    if (o.twistBones) {
      const lm = trAt(el.t + (wr.t - el.t) * 0.5);
      addJ('lowerArm_twist' + S, par, lm.c, lm.r * 0.9);
      par = 'lowerArm_twist' + S;
    }
    addJ('hand' + S, par, wr.c, Math.max(wr.r * 0.9, 0.022 * sc));

    if (o.fingers) fitHand(m, sc, s, S, wr.c, last.c, a, halfW, addJ, warn);
  }
  return J;
}

/** Digits as mesh components past the wrist (source "hand" step). */
function fitHand(
  m: Mesh,
  sc: number,
  s: 1 | -1,
  S: string,
  wrist: Vec3,
  armEnd: Vec3,
  armDir: Vec3,
  halfW: number,
  addJ: (name: string, parent: string | null, pos: Vec3, radius: number) => Vec3,
  warn: (s: string) => void,
): void {
  const P = m.P;
  let hd = norm(sub(armEnd, wrist));
  if (len(hd) < 0.5) hd = armDir;
  const FWD: Vec3 = [0, 0, -1];
  const hand: number[] = [];
  for (let i = 0; i < P.length; i++)
    if (dot(sub(P[i]!, wrist), hd) > 0 && dist(P[i]!, wrist) < 0.25 * sc && P[i]![0] * s > halfW)
      hand.push(i);
  const handSet = new Set(hand);
  const handTris: number[] = [];
  for (let t = 0; t + 2 < m.tris.length; t += 3)
    if ([0, 1, 2].some((k) => handSet.has(m.tris[t + k]!)))
      handTris.push(m.tris[t]!, m.tris[t + 1]!, m.tris[t + 2]!);
  const proj = (i: number) => dot(sub(P[i]!, wrist), hd);
  let pmax = 0;
  for (const i of hand) pmax = Math.max(pmax, proj(i));
  const maxProj = (c: number[]) => c.reduce((a, i) => Math.max(a, proj(i)), -Infinity);
  const cm = (ids: number[]) =>
    mean(
      ids.map((i) => P[i]!),
      wrist,
    );
  const pca = (ids: number[]): Vec3 => {
    const c = cm(ids);
    const C = [
      [0, 0, 0],
      [0, 0, 0],
      [0, 0, 0],
    ];
    for (const i of ids) {
      const dd = sub(P[i]!, c);
      for (let x = 0; x < 3; x++) for (let y = 0; y < 3; y++) C[x]![y]! += dd[x]! * dd[y]!;
    }
    let w = hd;
    for (let it = 0; it < 60; it++) {
      const nw = norm([0, 1, 2].map((r) => dot(C[r] as Vec3, w)) as Vec3);
      if (len(nw) < 0.5) break;
      w = nw;
    }
    return w;
  };
  // Digit-like: thin across its own long axis (the palm is wide; thumbs are angled).
  const digitLike = (c: number[]) => {
    const ax = pca(c);
    const u = norm(cross(ax, Math.abs(ax[1]) > 0.9 ? FWD : [0, 1, 0]));
    const v = cross(ax, u);
    let u0 = Infinity,
      u1 = -Infinity,
      v0 = Infinity,
      v1 = -Infinity;
    for (const i of c) {
      const pu = dot(P[i]!, u),
        pv = dot(P[i]!, v);
      u0 = Math.min(u0, pu);
      u1 = Math.max(u1, pu);
      v0 = Math.min(v0, pv);
      v1 = Math.max(v1, pv);
    }
    return Math.max(u1 - u0, v1 - v0) < 0.05 * sc;
  };
  const MINV = 6;
  let fingers: number[][] | null = null,
    tK: number | null = null;
  if (pmax > 0.04 * sc)
    for (let t = pmax - 0.015 * sc; t > pmax * 0.35; t -= 0.002 * sc) {
      const cs = components(
        m,
        hand.filter((i) => proj(i) > t),
      ).filter((c) => c.length >= MINV);
      const digits = cs.filter(digitLike);
      if (digits.length >= 4) {
        fingers = digits;
        tK = t;
      } else if (fingers) break;
    }
  if (fingers) fingers = [...fingers].sort((A, B) => maxProj(B) - maxProj(A)).slice(0, 4);
  const fingerSet = new Set(fingers?.flat() ?? []);
  let thumb: number[] | null = null;
  if (pmax > 0.04 * sc)
    for (let t = pmax * 0.15; t < (tK ?? pmax) - 0.01 * sc; t += 0.002 * sc) {
      const cs = components(
        m,
        hand.filter((i) => proj(i) > t),
      ).filter((c) => c.length >= MINV && !c.every((i) => fingerSet.has(i)));
      if (cs.length >= 2) {
        const main = cs[0]!;
        const small = cs.slice(1).find((c) => c.length > main.length * 0.03 && digitLike(c));
        if (small) {
          thumb = small;
          break;
        }
      }
    }
  if (fingers && thumb) {
    const ts = new Set(thumb);
    fingers = fingers.filter((c) => !c.some((i) => ts.has(i)));
  }
  if (fingers && fingers.length >= 4) {
    const tc = thumb ? cm(thumb) : null;
    // Order across the hand: by distance from the thumb (index first); without
    // a thumb, front-most first (the thumb side faces forward in A/T pose).
    if (tc) fingers.sort((A, B) => dist(cm(A), tc) - dist(cm(B), tc));
    else fingers.sort((A, B) => dot(cm(B), FWD) - dot(cm(A), FWD));
  }
  const place = (name: string, pts: Vec3[], rad: number) => {
    addJ(name + '1' + S, 'hand' + S, pts[0]!, rad * 1.05);
    addJ(name + '2' + S, name + '1' + S, pts[1]!, rad);
    addJ(name + '3' + S, name + '2' + S, pts[2]!, rad * 0.92);
    addJ(name + 'Tip' + S, name + '3' + S, pts[3]!, rad * 0.8);
  };
  const digit = (name: string, ids: number[], isThumb: boolean): boolean => {
    const ids_ = new Set(ids);
    const c = cm(ids);
    let ax = pca(ids);
    // Point from the hand towards the digit tip.
    if (dot(sub(c, wrist), ax) < 0) ax = mul(ax, -1);
    let ta = Infinity,
      tb = -Infinity;
    for (const i of ids) {
      const t = dot(P[i]!, ax);
      ta = Math.min(ta, t);
      tb = Math.max(tb, t);
    }
    const line: Loop[] = [];
    // Mid-interval samples (~every 3 mm at human scale): planes never pass
    // through the digit's extreme vertices, so sampling is scale-invariant.
    const nSteps = Math.max(2, Math.ceil((tb - ta) / (0.003 * sc)));
    for (let k = 0; k < nSteps; k++) {
      const t = ta + ((k + 0.5) * (tb - ta)) / nSteps;
      const loops = section(m, ax, t, handTris).filter(
        (l) => dist(l.c, c) < (tb - ta) * 0.75 + 0.01 * sc && l.verts.some((i) => ids_.has(i)),
      );
      if (loops.length) line.push(loops.sort((p, q) => p.r - q.r)[0]!);
    }
    if (line.length < 2) return false;
    const rad = Math.max(0.005 * sc, (line.reduce((acc, l) => acc + l.r, 0) / line.length) * 0.85);
    const base = add(line[0]!.c, mul(ax, -(isThumb ? 0.018 : 0.012) * sc));
    const tipP = add(line[line.length - 1]!.c, mul(ax, -0.002 * sc));
    const full = [base, ...line.map((l) => l.c), tipP];
    const L = [0];
    for (let k = 1; k < full.length; k++) L.push(L[k - 1]! + dist(full[k]!, full[k - 1]!));
    const alongF = (f: number): Vec3 => {
      const want = L[L.length - 1]! * f;
      for (let k = 1; k < full.length; k++)
        if (L[k]! >= want)
          return lerp(
            full[k - 1]!,
            full[k]!,
            (want - L[k - 1]!) / Math.max(L[k]! - L[k - 1]!, 1e-9),
          );
      return full[full.length - 1]!;
    };
    const fr = isThumb ? [0.42, 0.74] : [0.5, 0.78];
    place(name, [base, alongF(fr[0]!), alongF(fr[1]!), tipP], rad);
    return true;
  };
  // Template fallback: digits laid out across the hand, thumb on the front side.
  let side = sub(FWD, mul(hd, dot(FWD, hd)));
  side = len(side) > 1e-6 ? norm(side) : norm(cross(hd, [0, 1, 0]));
  const template = (name: string, k: number) => {
    const isThumb = name === 'thumb';
    const scale = isThumb ? 1 : ([1, 1.08, 1, 0.82][k] ?? 1);
    const base = isThumb
      ? add(add(wrist, mul(hd, 0.025 * sc)), mul(side, 0.03 * sc))
      : add(add(wrist, mul(hd, 0.095 * sc)), mul(side, (0.027 - k * 0.018) * sc));
    const dir = isThumb ? norm(add(mul(hd, 0.6), mul(side, 0.8))) : hd;
    const ls = (isThumb ? [0.04, 0.032, 0.028] : [0.045, 0.028, 0.022]).map((x) => x * sc * scale);
    const p1 = add(base, mul(dir, ls[0]!)),
      p2 = add(p1, mul(dir, ls[1]!)),
      p3 = add(p2, mul(dir, ls[2]!));
    place(name, [base, p1, p2, p3], 0.009 * sc);
  };
  if (!thumb || !digit('thumb', thumb, true)) {
    warn(`Thumb${S} not found; using template placement.`);
    template('thumb', 0);
  }
  const ok = fingers && fingers.length === 4;
  if (!ok) warn(`Fingers${S} not found (${fingers?.length ?? 0} of 4); using template placement.`);
  FINGERS.forEach((n, k) => {
    const ids = ok ? fingers![k] : undefined;
    if (!ids || !digit(n, ids, false)) {
      if (ok) warn(`Finger ${n}${S} not traced; using template placement.`);
      template(n, k);
    }
  });
}

// ---------------------------------------------------------------------------
// Quadruped

/**
 * Fits a generic four-legged skeleton: hips, spine…, chest, neck…, head,
 * head_end, (jaw), tail…, and per side front/hind UpperLeg, LowerLeg, Foot,
 * (Toe). The long body axis is the longest horizontal extent; the head end is
 * the bulkier end (the tail tapers).
 */
export function fitQuadruped(mesh: AutoRigMesh, options: FitQuadrupedOptions = {}): AutoRigResult {
  const o = quadDefaults(options);
  const warnings: string[] = [];
  const warn = (s: string) => {
    if (!warnings.includes(s)) warnings.push(s);
  };
  let m: Mesh = { P: [], tris: [] };
  try {
    m = weld(mesh);
  } catch {
    warn('Mesh could not be read; using template proportions.');
  }
  if (m.P.length < 4 || m.tris.length < 3) warn('Mesh is empty; using template proportions.');
  const b = bounds(m.P);
  const empty = !Number.isFinite(b.min[0]);
  const groundY = empty ? 0 : b.min[1];
  let H = b.max[1] - b.min[1];
  if (!(H > 1e-9) || !Number.isFinite(H)) H = 1;
  const centre: Vec3 = empty
    ? [0, 0, 0]
    : [(b.min[0] + b.max[0]) / 2, groundY, (b.min[2] + b.max[2]) / 2];
  const longX = !empty && b.max[0] - b.min[0] > b.max[2] - b.min[2];
  try {
    // Long axis -> canonical z (rotate a quarter turn when it is model X).
    let f = frame(centre, longX ? 3 : 0);
    let cm = canonMesh(m, f);
    let cb = bounds(cm.P);
    let Lb = Number.isFinite(cb.min[2]) ? cb.max[2] - cb.min[2] : 2 * H;
    if (!(Lb > 1e-9)) Lb = 2 * H;
    // Lateral midline from the body's mid sections.
    const ctrs: number[] = [];
    for (const k of [0.4, 0.5, 0.6]) {
      const z = (Number.isFinite(cb.min[2]) ? cb.min[2] : -Lb / 2) + Lb * k;
      const t = straddling(
        section(cm, [0, 0, 1], z),
        0.02 * Lb,
        (l) => (l.x1 - l.x0) * (l.y1 - l.y0),
      );
      if (t) ctrs.push((t.x0 + t.x1) / 2);
    }
    if (ctrs.length) {
      const dx = ctrs.reduce((a, x) => a + x, 0) / ctrs.length;
      const shift = toModel(f, [dx, 0, 0]);
      f = frame(shift, longX ? 3 : 0);
      cm = canonMesh(m, f);
      cb = bounds(cm.P);
    }
    // Head end: bulkier sections near the end (tails taper), tie-break by height.
    if (!empty) {
      const endScore = (sgn: 1 | -1) => {
        let mass = 0,
          top = -Infinity;
        for (const k of [0.05, 0.1, 0.15]) {
          const z = sgn > 0 ? cb.max[2] - k * Lb : cb.min[2] + k * Lb;
          for (const l of section(cm, [0, 0, 1], z)) if (l.c[1] > 0.3 * H) mass += l.r * l.r;
        }
        for (const p of cm.P)
          if ((sgn > 0 ? cb.max[2] - p[2] : p[2] - cb.min[2]) < 0.2 * Lb) top = Math.max(top, p[1]);
        return { mass, top };
      };
      const front = endScore(-1),
        back = endScore(1);
      let headBack: boolean;
      if (
        Math.max(front.mass, back.mass) > 0 &&
        Math.abs(front.mass - back.mass) > 0.15 * Math.max(front.mass, back.mass)
      )
        headBack = back.mass > front.mass;
      else if (Math.abs(front.top - back.top) > 0.05 * H) headBack = back.top > front.top;
      else {
        headBack = false;
        warn('Could not tell head from tail; assuming the head is towards the front.');
      }
      if (headBack) {
        f = frame(f.origin, (longX ? 3 : 0) + 2);
        cm = canonMesh(m, f);
        cb = bounds(cm.P);
      }
    }
    const fitted = fitQuadCanonical(cm, H, Lb, empty ? null : cb, o, warn).map((j) => ({
      ...j,
      position: toModel(f, j.position),
    }));
    const forward = norm(sub(toModel(f, [0, 0, -1]), toModel(f, [0, 0, 0])));
    return finish(fitted, forward, H, groundY, warnings);
  } catch (e) {
    warn(
      `Fitting failed (${e instanceof Error ? e.message : String(e)}); using template proportions.`,
    );
    const f = frame(centre, 0);
    const fitted = fitQuadCanonical({ P: [], tris: [] }, H, 2 * H, null, o, () => {}).map((j) => ({
      ...j,
      position: toModel(f, j.position),
    }));
    return finish(fitted, [0, 0, -1], H, groundY, warnings);
  }
}

type QuadOpts = ReturnType<typeof quadDefaults>;

/** Canonical frame: +Y up, ground y = 0, long axis z with the head towards -z, midline x = 0. */
function fitQuadCanonical(
  m: Mesh,
  H: number,
  Lb: number,
  cb: Bounds | null,
  o: QuadOpts,
  warn: (s: string) => void,
): FittedJoint[] {
  const P = m.P;
  const J: FittedJoint[] = [];
  const addJ = (name: string, parent: string | null, pos: Vec3, radius: number): Vec3 => {
    J.push({
      name,
      parent,
      position: [pos[0], pos[1], pos[2]],
      radius: Math.max(radius, 0.002 * Lb),
    });
    return pos;
  };
  const zMin = cb ? cb.min[2] : -Lb / 2,
    zMax = cb ? cb.max[2] : Lb / 2;
  const hcache = new Map<number, Loop[]>();
  const hsec = (y: number) => {
    let l = hcache.get(y);
    if (!l) hcache.set(y, (l = section(m, [0, 1, 0], y)));
    return l;
  };
  const zcache = new Map<number, Loop[]>();
  const zsec = (z: number) => {
    let l = zcache.get(z);
    if (!l) zcache.set(z, (l = section(m, [0, 0, 1], z)));
    return l;
  };
  const tol = 0.01 * Lb;
  const crossArea = (l: Loop) => (l.x1 - l.x0) * (l.y1 - l.y0);
  const torsoAt = (z: number) => straddling(zsec(z), tol, crossArea);
  const legLoop = (l: Loop) => l.x0 * l.x1 > 0;

  // ---- leg level: highest height with a front and a hind leg loop per side
  const quad = (y: number): (Loop | null)[] | null => {
    const res: (Loop | null)[] = [];
    for (const [s] of SIDES) {
      const ls = hsec(y)
        .filter((l) => legLoop(l) && l.c[0] * s > 0)
        .sort((a, bb) => a.c[2] - bb.c[2]);
      const fr = ls[0],
        hi = ls[ls.length - 1];
      if (!fr || !hi || hi.c[2] - fr.c[2] < 0.15 * Lb) return null;
      res.push(fr, hi);
    }
    return res;
  };
  let legTop = -1;
  for (let y = 0.02 * H; y < 0.8 * H; y += 0.01 * H) {
    if (quad(y)) legTop = y;
    else if (legTop >= 0) break;
  }
  if (legTop < 0) warn('Four legs not found; using template proportions.');
  const refY = legTop >= 0 ? legTop * 0.5 : 0.25 * H;
  const ref = legTop >= 0 ? quad(refY) : null;

  // ---- spine: torso sections above the legs
  interface LegFit {
    top: Vec3;
    topY: number;
    at: (y: number) => { c: Vec3; r: number };
    low: { c: Vec3; r: number; y: number };
  }
  const legs = new Map<string, LegFit>();
  const legKeys = ['front.L', 'hind.L', 'front.R', 'hind.R'];
  legKeys.forEach((key, k) => {
    const s = k < 2 ? 1 : -1;
    const isFront = k % 2 === 0;
    const start = ref?.[k] ?? null;
    const tz0 = zMin + Lb * (isFront ? 0.3 : 0.75);
    const fallbackC: Vec3 = [s * 0.1 * Lb, refY, tz0];
    const tr: { y: number; c: Vec3; r: number }[] = [];
    if (start) {
      tr.push({ y: refY, c: start.c, r: start.r });
      const step = 0.01 * H;
      let cur = start.c;
      const near = (y: number) =>
        hsec(y)
          .filter(
            (l) =>
              legLoop(l) &&
              l.c[0] * s > 0 &&
              Math.hypot(l.c[0] - cur[0], l.c[2] - cur[2]) < 2 * start.r + 0.02 * Lb,
          )
          .sort(
            (a, bb) =>
              Math.hypot(a.c[0] - cur[0], a.c[2] - cur[2]) -
              Math.hypot(bb.c[0] - cur[0], bb.c[2] - cur[2]),
          )[0];
      for (let y = refY + step; y < H; y += step) {
        const l = near(y);
        if (!l || l.r > 2.5 * start.r) break;
        tr.push({ y, c: l.c, r: l.r });
        cur = l.c;
      }
      cur = start.c;
      for (let y = refY - step; y > 0.005 * H; y -= step) {
        const l = near(y);
        if (!l || l.r > 3 * start.r) break;
        tr.unshift({ y, c: l.c, r: l.r });
        cur = l.c;
      }
    } else tr.push({ y: refY, c: fallbackC, r: 0.04 * Lb });
    const topE = tr[tr.length - 1]!;
    const lowE = tr[0]!;
    const at = (y: number) => {
      const e = tr.reduce((bb, x) => (Math.abs(x.y - y) < Math.abs(bb.y - y) ? x : bb), tr[0]!);
      return { c: [e.c[0], y, e.c[2]] as Vec3, r: e.r };
    };
    legs.set(key, { top: topE.c, topY: start ? Math.max(topE.y, legTop) : 0.4 * H, at, low: lowE });
  });
  const avgZ = (a: string, bKey: string) => (legs.get(a)!.top[2] + legs.get(bKey)!.top[2]) / 2;
  const hipsZ = avgZ('hind.L', 'hind.R'),
    chestZ = avgZ('front.L', 'front.R');
  const bodyY = (z: number, fb: number) => {
    const t = torsoAt(z);
    return t
      ? { y: t.c[1] + 0.3 * (t.y1 - t.c[1]), r: t.r, c: t.c }
      : { y: fb, r: 0.1 * Lb, c: [0, fb, z] as Vec3 };
  };
  const bodyFallbackY = (legTop >= 0 ? legTop : 0.5 * H) + 0.15 * H;
  const spineN = ['hips', ...chain('spine', o.spineCount), 'chest'];
  spineN.forEach((n, k) => {
    const z = hipsZ + (chestZ - hipsZ) * (k / (spineN.length - 1));
    const bd = bodyY(z, bodyFallbackY);
    addJ(n, k ? spineN[k - 1]! : null, [0, bd.y, z], bd.r * 0.8);
  });
  if (!torsoAt(hipsZ) || !torsoAt(chestZ))
    warn('Body sections not found; using template proportions.');

  // ---- track straddling loops along z from a start towards an end
  const trackZ = (z0: number, dir: 1 | -1, zEnd: number) => {
    const step = 0.01 * Lb;
    const st = torsoAt(z0);
    const path: { z: number; c: Vec3; r: number }[] = [];
    if (!st) return path;
    path.push({ z: z0, c: st.c, r: st.r });
    let cur = st.c,
      rPrev = st.r;
    for (let z = z0 + dir * step; dir > 0 ? z < zEnd : z > zEnd; z += dir * step) {
      const l = zsec(z)
        .filter((x) => x.x0 < tol && x.x1 > -tol && dist(x.c, cur) < Math.max(3 * rPrev, 0.1 * Lb))
        .sort((p, q) => dist(p.c, cur) - dist(q.c, cur))[0];
      if (!l) break;
      path.push({ z, c: l.c, r: l.r });
      cur = l.c;
      rPrev = l.r;
    }
    return path;
  };
  const extreme = (end: Vec3, dir: 1 | -1, r: number): Vec3 => {
    let best: Vec3 | null = null;
    for (const p of P)
      if (
        Math.hypot(p[0] - end[0], p[1] - end[1]) < 3 * r + 0.02 * Lb &&
        (p[2] - end[2]) * dir >= 0
      )
        if (!best || p[2] * dir > best[2] * dir) best = p;
    return best ? [0, end[1], best[2]] : end;
  };
  const along = (pts: Vec3[], f: number): Vec3 => {
    if (pts.length < 2) return pts[0] ?? [0, 0, 0];
    const L = [0];
    for (let k = 1; k < pts.length; k++) L.push(L[k - 1]! + dist(pts[k]!, pts[k - 1]!));
    const want = L[L.length - 1]! * clamp(f, 0, 1);
    for (let k = 1; k < pts.length; k++)
      if (L[k]! >= want)
        return lerp(pts[k - 1]!, pts[k]!, (want - L[k - 1]!) / Math.max(L[k]! - L[k - 1]!, 1e-12));
    return pts[pts.length - 1]!;
  };
  const chestPos = J.find((j) => j.name === 'chest')!.position;
  const hipsPos = J.find((j) => j.name === 'hips')!.position;

  // ---- neck + head: towards -z from the chest
  const np = trackZ(chestZ, -1, zMin);
  let neckPts: Vec3[], headPos: Vec3, snout: Vec3, headR: number;
  if (np.length >= 4) {
    const lastE = np[np.length - 1]!;
    snout = extreme(lastE.c, -1, lastE.r);
    const lo = Math.floor(np.length * 0.25),
      hi = Math.max(lo + 1, Math.ceil(np.length * 0.8));
    let kN = lo;
    for (let k = lo; k < hi && k < np.length; k++) if (np[k]!.r < np[kN]!.r) kN = k;
    // Head pivot at the top of the thinnest stretch of neck.
    let kH = kN;
    while (kH + 1 < np.length - 1 && np[kH + 1]!.r < np[kN]!.r * 1.15) kH++;
    let kS = np.findIndex((e) => e.r < 0.8 * np[0]!.r);
    if (kS < 0 || kS >= kH) kS = Math.floor(kH * 0.25);
    neckPts = np.slice(kS, kH + 1).map((e) => [0, e.c[1], e.c[2]] as Vec3);
    headPos = [0, np[kH]!.c[1], np[kH]!.c[2]];
    headR = np.slice(kH).reduce((a, e) => Math.max(a, e.r), 0);
  } else {
    warn('Neck / head not traced; using template proportions.');
    headPos = [0, chestPos[1] + 0.15 * H, zMin + 0.2 * Lb];
    snout = [0, headPos[1], zMin];
    neckPts = [lerp(chestPos, headPos, 0.25), headPos];
    headR = 0.06 * Lb;
  }
  if (neckPts.length < 2) neckPts = [lerp(chestPos, headPos, 0.3), headPos];
  const neckN = chain('neck', o.neckCount);
  neckN.forEach((n, k) => {
    addJ(n, k ? neckN[k - 1]! : 'chest', along(neckPts, k / neckN.length), headR * 0.7);
  });
  addJ('head', neckN[neckN.length - 1]!, headPos, headR);
  addJ('head_end', 'head', snout, headR * 0.4);
  if (o.jaw)
    addJ('jaw', 'head', [0, headPos[1] - headR * 0.4, lerp(headPos, snout, 0.2)[2]], headR * 0.5);

  // ---- tail: towards +z from the hips
  if (o.tailCount > 0) {
    const tp = trackZ(hipsZ, 1, zMax + 0.01 * Lb);
    let tailPts: Vec3[] = [];
    if (tp.length >= 2) {
      let kB = tp.findIndex((e) => e.r < 0.6 * tp[0]!.r);
      if (kB < 0) kB = tp.length - 1;
      const lastE = tp[tp.length - 1]!;
      const tip = extreme(lastE.c, 1, lastE.r);
      tailPts = [...tp.slice(Math.max(0, kB - 1)).map((e) => [0, e.c[1], e.c[2]] as Vec3), tip];
      if (dist(tailPts[0]!, tip) < 0.05 * Lb) {
        warn('No tail found; tail joints placed behind the hips.');
        tailPts = [];
      }
    }
    if (tailPts.length < 2) {
      warn('No tail found; tail joints placed behind the hips.');
      const base: Vec3 = [0, hipsPos[1], Math.max(hipsPos[2], zMax - 0.1 * Lb)];
      tailPts = [base, add(base, [0, 0, 0.1 * Lb])];
    }
    const tailN = chain('tail', o.tailCount);
    tailN.forEach((n, k) =>
      addJ(n, k ? tailN[k - 1]! : 'hips', along(tailPts, k / tailN.length), 0.02 * Lb),
    );
  }

  // ---- legs: upper (shoulder / hip) in the body, lower (elbow / knee), foot, toe
  for (const [, S] of SIDES) {
    for (const isFront of [true, false]) {
      const key = (isFront ? 'front' : 'hind') + S;
      const leg = legs.get(key)!;
      const prefix = isFront ? 'front' : 'hind';
      const topY = leg.topY;
      const body = bodyY(leg.top[2], bodyFallbackY);
      const upper: Vec3 = [leg.top[0], topY + (Math.max(body.c[1], topY) - topY) * 0.5, leg.top[2]];
      const lower = leg.at(topY * 0.6);
      const foot = leg.at(topY * 0.22);
      addJ(prefix + 'UpperLeg' + S, isFront ? 'chest' : 'hips', upper, lower.r * 1.2);
      addJ(prefix + 'LowerLeg' + S, prefix + 'UpperLeg' + S, lower.c, lower.r);
      addJ(prefix + 'Foot' + S, prefix + 'LowerLeg' + S, foot.c, foot.r);
      if (o.toes) {
        const lowC = leg.low.c,
          rad = leg.low.r;
        let toe: Vec3 | null = null;
        for (const p of P)
          if (
            p[1] < Math.max(0.03 * H, foot.c[1] * 0.3) &&
            Math.hypot(p[0] - lowC[0], p[2] - lowC[2]) < 3 * rad + 0.02 * Lb
          )
            if (!toe || p[2] < toe[2]) toe = p;
        if (!toe) warn(`Toe of ${prefix} leg${S} not found; using template placement.`);
        const tp: Vec3 = toe
          ? [lowC[0], Math.max(0.2 * foot.c[1], 0.005 * H), toe[2] + 0.25 * rad]
          : [foot.c[0], 0.2 * foot.c[1], foot.c[2] - 0.05 * Lb];
        addJ(prefix + 'Toe' + S, prefix + 'Foot' + S, tp, rad * 0.6);
      }
    }
  }
  return J;
}

// ---------------------------------------------------------------------------
// Bind orientation + symmetrize

/** Picks the child a joint should point at (spine over legs, middle finger, toes). */
function mainChild(j: FittedJoint, kids: FittedJoint[]): FittedJoint | undefined {
  const suffix = (n: string) => (n.endsWith('.L') || n.endsWith('.R') ? n.slice(-2) : '');
  const same = kids.filter((k) => suffix(k.name) === suffix(j.name));
  const pool = same.length ? same : kids;
  return pool.find((k) => /^(middle1|toes|neck|head)\b/.test(k.name)) ?? pool[0];
}

/**
 * Converts fitted joints to RigJoints with global bind transforms. Each joint's
 * local +Y points at its main child; roll keeps local +Z towards the
 * character's forward (or up when the bone points along forward). Leaves take
 * their parent's orientation.
 */
export function toRigJoints(
  fitted: FittedJoint[],
  orient: { forward?: Vec3; up?: Vec3 } = {},
): RigJoint[] {
  const fwd = norm(orient.forward ?? [0, 0, -1]),
    up = norm(orient.up ?? [0, 1, 0]);
  const forward = len(fwd) > 0.5 ? fwd : ([0, 0, -1] as Vec3);
  const upV = len(up) > 0.5 ? up : ([0, 1, 0] as Vec3);
  const byName = new Map(fitted.map((j) => [j.name, j]));
  const kids = new Map<string, FittedJoint[]>();
  for (const j of fitted)
    if (j.parent) {
      const l = kids.get(j.parent);
      if (l) l.push(j);
      else kids.set(j.parent, [j]);
    }
  const rot = new Map<string, Vec3>();
  const rotation = (j: FittedJoint, depth = 0): Vec3 => {
    const done = rot.get(j.name);
    if (done) return done;
    let r: Vec3 = [0, 0, 0];
    const child = mainChild(j, kids.get(j.name) ?? []);
    const y = child ? norm(sub(child.position, j.position)) : ([0, 0, 0] as Vec3);
    if (len(y) > 0.5) {
      let z = sub(forward, mul(y, dot(forward, y)));
      if (len(z) < 0.2) z = sub(upV, mul(y, dot(upV, y)));
      if (len(z) < 0.2) z = sub([1, 0, 0], mul(y, y[0]));
      z = norm(z);
      const x = cross(y, z);
      const d = decompose([...x, 0, ...y, 0, ...z, 0, 0, 0, 0, 1]).rotation;
      if (finite(d)) r = d;
    } else {
      const p = j.parent ? byName.get(j.parent) : undefined;
      if (p && depth < fitted.length) r = rotation(p, depth + 1);
    }
    rot.set(j.name, r);
    return r;
  };
  return fitted.map((j) => ({
    name: j.name,
    parent: j.parent,
    bind: { position: [...j.position], rotation: [...rotation(j)], scale: [1, 1, 1] },
  }));
}

function finish(
  fitted: FittedJoint[],
  forward: Vec3,
  H: number,
  groundY: number,
  warnings: string[],
): AutoRigResult {
  // Guarantee finite positions and unique names whatever happened upstream.
  const seen = new Set<string>();
  const out: FittedJoint[] = [];
  for (const j of fitted) {
    if (seen.has(j.name)) continue;
    seen.add(j.name);
    let p = j.position;
    if (!finite(p)) {
      const par = out.find((q) => q.name === j.parent);
      p = par ? [...par.position] : [0, groundY, 0];
      if (!warnings.includes('Some joints could not be placed.'))
        warnings.push('Some joints could not be placed.');
    }
    out.push({ ...j, position: p, radius: Number.isFinite(j.radius) ? j.radius : 0.01 * H });
  }
  return {
    joints: toRigJoints(out, { forward }),
    fitted: out,
    warnings,
    forward,
    height: H,
    groundY,
  };
}

/** 3x3 rotation (column-major columns) from the rig-doc Euler XYZ degrees. */
function rotColumns(r: Vec3): [Vec3, Vec3, Vec3] {
  const [x, y, z] = r.map((v) => (v * Math.PI) / 180) as Vec3;
  const [cx, sx, cy, sy, cz, sz] = [
    Math.cos(x),
    Math.sin(x),
    Math.cos(y),
    Math.sin(y),
    Math.cos(z),
    Math.sin(z),
  ];
  return [
    [cy * cz, cx * sz + sx * sy * cz, sx * sz - cx * sy * cz],
    [-cy * sz, cx * cz - sx * sy * sz, sx * cz + cx * sy * sz],
    [sy, -sx * cy, cx * cy],
  ];
}

/**
 * Mirrors the `side` joints onto their `.L`/`.R` partners about the plane
 * x = planeX (default: the midline of the existing pairs, else of the centre
 * joints). Rotations are mirrored as S·R·S (S = diag(-1, 1, 1)), keeping a
 * proper rotation. Unpaired joints without a side suffix are centred on the
 * plane; suffixed joints without a partner are left alone.
 */
export function symmetrize(joints: RigJoint[], side: 'L' | 'R', planeX?: number): RigJoint[] {
  const byName = new Map(joints.map((j) => [j.name, j]));
  const px = (j: RigJoint) => j.bind?.position?.[0] ?? 0;
  let plane = planeX;
  if (plane === undefined || !Number.isFinite(plane)) {
    const mids: number[] = [];
    for (const j of joints)
      if (j.name.endsWith('.L')) {
        const o = byName.get(mirrorName(j.name)!);
        if (o) mids.push((px(j) + px(o)) / 2);
      }
    const centre = joints.filter((j) => !mirrorName(j.name)).map(px);
    const src = mids.length ? mids : centre;
    plane = src.length ? src.reduce((a, x) => a + x, 0) / src.length : 0;
  }
  const from = '.' + side;
  return joints.map((j) => {
    const other = mirrorName(j.name);
    const bind = j.bind ?? {};
    if (!other) {
      if (!bind.position) return structuredClone(j);
      const p = bind.position;
      return {
        ...structuredClone(j),
        bind: { ...structuredClone(bind), position: [plane!, p[1], p[2]] },
      };
    }
    if (j.name.endsWith(from)) return structuredClone(j);
    const srcJ = byName.get(other);
    if (!srcJ) return structuredClone(j);
    const sb = srcJ.bind ?? {};
    const p = sb.position ?? [0, 0, 0];
    const next: RigJoint = {
      ...structuredClone(j),
      bind: {
        position: [2 * plane! - p[0], p[1], p[2]],
        rotation: mirrorRotation(sb.rotation ?? [0, 0, 0]),
        scale: [...(sb.scale ?? [1, 1, 1])],
      },
    };
    return next;
  });
}

function mirrorRotation(r: Vec3): Vec3 {
  const [X, Y, Z] = rotColumns(r);
  const S = (v: Vec3): Vec3 => [-v[0], v[1], v[2]];
  // S·R·S: columns become (-S·X, S·Y, S·Z).
  const nx = mul(S(X), -1),
    ny = S(Y),
    nz = S(Z);
  const d = decompose([...nx, 0, ...ny, 0, ...nz, 0, 0, 0, 0, 1]).rotation;
  return finite(d) ? d : [0, 0, 0];
}

/** Joint names `fitHuman` produces for the given options (in output order). */
export function humanJointNames(options: FitHumanOptions = {}): string[] {
  return fitHumanCanonical({ P: [], tris: [] }, 1.75, humanDefaults(options), () => {}).map(
    (j) => j.name,
  );
}
/** Joint names `fitQuadruped` produces for the given options (in output order). */
export function quadrupedJointNames(options: FitQuadrupedOptions = {}): string[] {
  return fitQuadCanonical({ P: [], tris: [] }, 1, 2, null, quadDefaults(options), () => {}).map(
    (j) => j.name,
  );
}
